import React, { useEffect, useState } from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Loader2 } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { supabase } from "~/integrations/supabase/client";
import { formatCurrency } from "~/utils/helpers";

interface ManualFactura {
  id: string;
  doc_number: string | null;
  doc_type: string | null;
  description: string | null;
  dues: number | null;
  paid: number | null;
  balance: number | null;
  payment_due_date: string | null;
  created_at: string;
}

interface ManualFacturaSelectorProps {
  clientId?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  error?: string;
}

const ManualFacturaSelector: React.FC<ManualFacturaSelectorProps> = ({
  clientId,
  value,
  onChange,
  error,
}) => {
  const [manualFacturas, setManualFacturas] = useState<ManualFactura[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchManualFacturas();
    } else {
      setManualFacturas([]);
      onChange(undefined);
    }
  }, [clientId]);

  const fetchManualFacturas = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("manual_facturas")
        .select(
          "id, doc_number, doc_type, description, dues, paid, balance, payment_due_date, created_at"
        )
        .eq("client_id", clientId)
        .gt("balance", 0) // Only show facturas with pending balance
        .order("created_at", { ascending: false });

      if (error) throw error;

      setManualFacturas(data || []);
    } catch (err) {
      console.error("Error fetching manual facturas:", err);
      setManualFacturas([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to get document type display name
  const getDocTypeDisplayName = (docType: string | null): string => {
    switch (docType) {
      case "FV":
        return "Factura de Venta";
      case "PL":
        return "Pagare";
      case "NC":
        return "Nota de Crédito";
      case "ND":
        return "Nota de Débito";
      default:
        return docType || "N/A";
    }
  };

  const formatManualFacturaOption = (factura: ManualFactura): string => {
    const docDisplay = factura.doc_number
      ? `${factura.doc_number}`
      : `ID: ${factura.id.substring(0, 8)}...`;
    const typeDisplay = getDocTypeDisplayName(factura.doc_type);
    const balanceDisplay = formatCurrency(factura.balance || 0);
    const dueDate = factura.payment_due_date
      ? format(new Date(factura.payment_due_date), "dd/MM/yyyy", { locale: es })
      : "Sin fecha";

    return `${docDisplay} | ${typeDisplay} | Saldo: $${balanceDisplay} | Vence: ${dueDate}`;
  };

  if (!clientId) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="Primero seleccione un cliente" />
        </SelectTrigger>
      </Select>
    );
  }

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={onChange} disabled={loading}>
        <SelectTrigger className={error ? "border-red-500" : ""}>
          <SelectValue
            placeholder={
              loading ? "Cargando facturas..." : "Seleccionar factura manual"
            }
          />
        </SelectTrigger>
        <SelectContent>
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Cargando facturas...</span>
            </div>
          ) : manualFacturas.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No hay facturas manuales pendientes para este cliente
            </div>
          ) : (
            manualFacturas.map((factura) => (
              <SelectItem key={factura.id} value={factura.id}>
                <div className="w-full">
                  <div className="font-medium">
                    {formatManualFacturaOption(factura)}
                  </div>
                  {factura.description && (
                    <div className="truncate text-xs text-gray-500">
                      {factura.description}
                    </div>
                  )}
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default ManualFacturaSelector;
